/**
 * The OCR backend, behind an interface so it can be replaced.
 *
 * Tesseract is what ships because it is the only mature recogniser that runs
 * entirely on the device today. Nothing downstream knows that: the parser, the
 * consensus merge and the validator all speak `OCRResult`, so a WebGPU or ONNX
 * recogniser can be added by implementing `OCREngine` and changing one line.
 *
 * EVERYTHING IS SELF-HOSTED
 * -------------------------
 * Tesseract.js defaults to fetching its worker, its WebAssembly core and its
 * language model from a CDN at first use. That is unacceptable twice over: this
 * site's Content-Security-Policy allows connections to its own origin only, and
 * the whole point of the feature is that a photograph of someone's shopping
 * never leaves their device — which is not a claim worth making if the scan
 * silently depends on a third party being reachable. All three assets are
 * served from `/tesseract` and `/tessdata` and cached by the service worker.
 */

import type { OCREngine, OCRLine, OCRResult, OCRWord } from './types';

/** Where the vendored runtime lives, relative to the site root. */
const WORKER_PATH = '/tesseract/worker.min.js';
const CORE_SIMD = '/tesseract/tesseract-core-simd-lstm.wasm.js';
const CORE_PLAIN = '/tesseract/tesseract-core-lstm.wasm.js';
const LANG_PATH = '/tessdata';

/**
 * Characters a nutrition panel can contain.
 *
 * Restricting the alphabet measurably improves accuracy, because it removes
 * whole classes of confusion the recogniser would otherwise have to weigh —
 * there is no currency symbol on a nutrition panel, so a badly-printed 5 can
 * never come back as an S-like glyph from that set.
 */
const CHARSET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789.,%()/<>+-: µ';

interface TesseractWord {
  text: string;
  confidence: number;
  bbox: { x0: number; y0: number; x1: number; y1: number };
}

interface TesseractLine {
  text: string;
  confidence: number;
  words: TesseractWord[];
  bbox: { x0: number; y0: number; x1: number; y1: number };
}

/** The slice of tesseract.js's worker this module actually uses. */
interface RecogniserWorker {
  recognize: (
    image: unknown,
    options?: unknown,
    output?: unknown,
  ) => Promise<{
    data: { lines?: TesseractLine[]; words?: TesseractWord[]; confidence: number };
  }>;
  terminate: () => Promise<void>;
  setParameters: (params: Record<string, string>) => Promise<unknown>;
}

export class TesseractOCREngine implements OCREngine {
  readonly name = 'tesseract';

  private worker: RecogniserWorker | null = null;
  private ready: Promise<void> | null = null;

  async initialize(onProgress?: (fraction: number) => void): Promise<void> {
    // Idempotent: several scans in a row must not each pay the model download.
    if (this.ready !== null) return this.ready;

    this.ready = (async () => {
      const { createWorker } = await import('tesseract.js');
      const worker = (await createWorker('eng', 1, {
        workerPath: WORKER_PATH,
        langPath: LANG_PATH,
        // The vendored model is the uncompressed `.traineddata`, so the
        // library must not look for a `.gz` that is not there.
        gzip: false,
        corePath: supportsSimd() ? CORE_SIMD : CORE_PLAIN,
        logger: (m: { status: string; progress: number }) => {
          if (onProgress && typeof m.progress === 'number') onProgress(m.progress);
        },
      })) as unknown as RecogniserWorker;

      await worker.setParameters({
        tessedit_char_whitelist: CHARSET,
        // A nutrition panel is a block of text in rows, not scattered words or
        // a single line. Telling the engine that improves its line grouping,
        // which is what the table reconstruction downstream depends on.
        tessedit_pageseg_mode: '6',
        preserve_interword_spaces: '1',
      });

      this.worker = worker;
    })();

    return this.ready;
  }

  async recognize(image: ImageData): Promise<OCRResult> {
    if (this.worker === null) await this.initialize();
    const worker = this.worker;
    if (worker === null) throw new Error('OCR engine failed to start');

    // Tesseract.js accepts an ImageData-like object directly, which keeps the
    // pixels in memory rather than routing them through a canvas.
    const { data } = await worker.recognize(image, {}, { blocks: true, text: true });

    const lines: OCRLine[] = (data.lines ?? []).map((line) => ({
      text: line.text.trim(),
      confidence: (line.confidence ?? 0) / 100,
      x: line.bbox.x0,
      y: line.bbox.y0,
      width: line.bbox.x1 - line.bbox.x0,
      height: line.bbox.y1 - line.bbox.y0,
      words: (line.words ?? []).map(toWord),
    }));

    const words: OCRWord[] =
      lines.length > 0 ? lines.flatMap((l) => l.words) : (data.words ?? []).map(toWord);

    return {
      words,
      lines,
      overallConfidence: (data.confidence ?? 0) / 100,
      variant: 'unknown',
    };
  }

  async terminate(): Promise<void> {
    const worker = this.worker;
    this.worker = null;
    this.ready = null;
    if (worker !== null) await worker.terminate();
  }
}

function toWord(w: TesseractWord): OCRWord {
  return {
    text: w.text,
    confidence: (w.confidence ?? 0) / 100,
    x: w.bbox.x0,
    y: w.bbox.y0,
    width: w.bbox.x1 - w.bbox.x0,
    height: w.bbox.y1 - w.bbox.y0,
  };
}

/**
 * Whether the browser can run the SIMD core, which is roughly twice as fast.
 *
 * Detected by assembling the smallest module that uses a SIMD opcode and asking
 * the engine to validate it — the only reliable test, since there is no feature
 * flag for it.
 */
export function supportsSimd(): boolean {
  try {
    return WebAssembly.validate(
      new Uint8Array([
        0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10,
        10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11,
      ]),
    );
  } catch {
    return false;
  }
}
